import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Edit, Trash2, X, Share2, Copy, FileText, Upload, Download } from "lucide-react";
import ShareModal from "@/components/ShareModal";

const empty = {
  title: "", locality: "", service_type: "continuous", schedule: "",
  requirements: "", start_date: "", end_date: "", slots: 1, hours: "",
  monthly_avg: "", total_value: "", referral_eligible: true, status: "open",
};

const DRAFT_KEY = "caring.vacancy_draft.v1";

function loadDraft() {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Ignore drafts that are essentially the empty form
    const hasContent = parsed && (parsed.title || parsed.locality || parsed.schedule || parsed.requirements);
    return hasContent ? parsed : null;
  } catch { return null; }
}

export default function AdminVacancies() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [share, setShare] = useState(null);
  const [busy, setBusy] = useState(false);
  const [hasDraft, setHasDraft] = useState(!!loadDraft());
  const [importOpen, setImportOpen] = useState(false);
  const [importJson, setImportJson] = useState("");
  const [importNotify, setImportNotify] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const load = () => api.get("/vacancies").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);

  // Auto-save the "new vacancy" draft as user types
  useEffect(() => {
    if (editing && !editing.id) {
      try { localStorage.setItem(DRAFT_KEY, JSON.stringify(editing)); } catch {}
    }
  }, [editing]);

  const openNew = () => {
    const draft = loadDraft();
    if (draft) {
      setEditing({ ...empty, ...draft });
      toast.info("Rascunho anterior recuperado", { duration: 3500 });
    } else {
      setEditing({ ...empty });
    }
  };
  const openEdit = (v) => setEditing({ ...empty, ...v });
  const closeEdit = () => {
    // Keep the draft (for new vacancies) — do not clear on close so nothing is lost.
    setEditing(null);
    setHasDraft(!!loadDraft());
  };
  const discardDraft = () => {
    try { localStorage.removeItem(DRAFT_KEY); } catch {}
    setHasDraft(false);
    if (editing && !editing.id) setEditing({ ...empty });
    toast.success("Rascunho descartado");
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = { ...editing };
      body.slots = Number(body.slots) || 1;
      body.monthly_avg = body.monthly_avg === "" ? null : Number(body.monthly_avg);
      body.total_value = body.total_value === "" ? null : Number(body.total_value);
      body.end_date = body.end_date || null;
      if (body.id) {
        const id = body.id; delete body.id; delete body._id; delete body.share_code;
        delete body.views; delete body.clicks; delete body.shares; delete body.slots_filled; delete body.created_at;
        await api.patch(`/vacancies/${id}`, body);
      } else {
        delete body.id; delete body.status;
        await api.post("/vacancies", body);
        try { localStorage.removeItem(DRAFT_KEY); } catch {}
        setHasDraft(false);
      }
      toast.success("Vaga guardada");
      setEditing(null); load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const remove = async (v) => {
    if (!window.confirm(`Eliminar vaga '${v.title}'?`)) return;
    await api.delete(`/vacancies/${v.id}`);
    toast.success("Vaga eliminada");
    load();
  };

  const duplicate = async (v) => {
    try {
      await api.post(`/vacancies/${v.id}/duplicate`);
      toast.success("Vaga duplicada");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const openImport = () => {
    setImportJson("");
    setImportResult(null);
    setImportNotify(false);
    setImportOpen(true);
  };
  const closeImport = () => setImportOpen(false);

  const downloadTemplate = () => {
    const sample = {
      vacancies: [
        {
          title: "Ajudante Familiar — Cidade",
          locality: "Cidade",
          service_type: "continuous",
          schedule: "Segunda a Sexta, período diurno",
          requirements: "Descrição do serviço.\n\nRequisitos e perfil desejado.",
          start_date: null,
          end_date: null,
          slots: 2,
          hours: null,
          monthly_avg: null,
          total_value: null,
          referral_eligible: true,
        },
      ],
      notify: false,
    };
    const blob = new Blob([JSON.stringify(sample, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "vagas_template.json";
    a.click();
    URL.revokeObjectURL(url);
  };

  const submitImport = async () => {
    let payload;
    try { payload = JSON.parse(importJson); }
    catch { toast.error("JSON inválido — verifique a sintaxe."); return; }
    if (!payload || !Array.isArray(payload.vacancies) || payload.vacancies.length === 0) {
      toast.error('O JSON tem de ter o campo "vacancies" com pelo menos 1 vaga.');
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/vacancies/bulk-import", { ...payload, notify: importNotify });
      setImportResult(data);
      toast.success(`Importação concluída — ${data.created_count} criada(s), ${data.skipped_count} ignorada(s).`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const inputCls = "w-full px-3 py-2.5 rounded-xl bg-[var(--muted)] border border-[var(--border)] focus:outline-none focus:ring-2 focus:ring-[var(--forest)]";

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="eyebrow">Gestão</div>
          <h1 className="font-heading text-5xl mt-2">Vagas</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {hasDraft && !editing && (
            <button onClick={openNew} className="pill-btn pill-ghost" data-testid="resume-draft-btn" title="Retomar o rascunho de vaga guardado">
              <FileText className="w-4 h-4"/> Retomar rascunho
            </button>
          )}
          <button onClick={openImport} className="pill-btn pill-ghost" data-testid="admin-import-vacancies-btn" title="Importar várias vagas de uma vez (JSON)">
            <Upload className="w-4 h-4"/> Importar JSON
          </button>
          <button onClick={openNew} className="pill-btn pill-primary" data-testid="admin-new-vacancy-btn">
            <Plus className="w-4 h-4"/> Nova vaga
          </button>
        </div>
      </div>

      <div className="card-surface overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[var(--muted)] text-left">
              <th className="px-4 py-3">Título</th><th className="px-4 py-3">Localidade</th>
              <th className="px-4 py-3">Tipo</th><th className="px-4 py-3">Estado</th>
              <th className="px-4 py-3">Preenchidas</th><th className="px-4 py-3 text-right">Ações</th>
            </tr>
          </thead>
          <tbody>
            {items.map((v) => (
              <tr key={v.id} className="border-t border-[var(--border)]">
                <td className="px-4 py-3 font-medium">{v.title}</td>
                <td className="px-4 py-3">{v.locality}</td>
                <td className="px-4 py-3">
                  <span className={`chip ${v.service_type === "continuous" ? "chip-green" : "chip-blue"}`}>
                    {v.service_type === "continuous" ? "🟢 Continuidade" : "🔵 Pontual"}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <span className={`chip ${v.status === "open" ? "chip-green" : "chip-neutral"}`}>{v.status}</span>
                </td>
                <td className="px-4 py-3">{v.slots_filled}/{v.slots}</td>
                <td className="px-4 py-3 text-right">
                  <div className="flex justify-end gap-1">
                    <button onClick={() => setShare(v)} className="pill-btn pill-ghost !py-1 !px-2" title="Partilhar" data-testid={`share-vacancy-${v.id}`}><Share2 className="w-3.5 h-3.5"/></button>
                    <button onClick={() => duplicate(v)} className="pill-btn pill-ghost !py-1 !px-2" title="Duplicar" data-testid={`duplicate-vacancy-${v.id}`}><Copy className="w-3.5 h-3.5"/></button>
                    <button onClick={() => openEdit(v)} className="pill-btn pill-ghost !py-1 !px-2" title="Editar" data-testid={`edit-vacancy-${v.id}`}><Edit className="w-3.5 h-3.5"/></button>
                    <button onClick={() => remove(v)} className="pill-btn pill-ghost !py-1 !px-2 !text-red-600" title="Eliminar"><Trash2 className="w-3.5 h-3.5"/></button>
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-[var(--ink-soft)]">Sem vagas.</td></tr>}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm grid place-items-center p-4">
          <form onSubmit={save} className="bg-white rounded-3xl w-full max-w-2xl overflow-hidden shadow-xl">
            <div className="p-6 flex items-center justify-between border-b border-[var(--border)]">
              <h2 className="font-heading text-2xl">{editing.id ? "Editar vaga" : "Nova vaga"}</h2>
              <button type="button" onClick={closeEdit} className="pill-btn pill-ghost !py-2 !px-3" data-testid="close-vacancy-modal"><X className="w-4 h-4"/></button>
            </div>
            {!editing.id && (
              <div className="px-6 pt-4">
                <div className="rounded-xl bg-[rgba(45,90,39,0.08)] border border-[rgba(45,90,39,0.15)] p-3 text-xs text-[var(--ink)] flex items-center justify-between gap-3">
                  <span>Rascunho guardado automaticamente enquanto preenche — pode fechar e retomar mais tarde sem perder nada.</span>
                  <button type="button" onClick={discardDraft} className="text-[var(--terracotta)] font-medium hover:underline" data-testid="discard-draft-btn">Limpar</button>
                </div>
              </div>
            )}
            <div className="p-6 grid md:grid-cols-2 gap-4 max-h-[70vh] overflow-y-auto">
              <div className="md:col-span-2"><label className="text-sm font-medium">Título</label><input required value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} className={`mt-1.5 ${inputCls}`} data-testid="vac-title-input"/></div>
              <div><label className="text-sm font-medium">Localidade</label><input required value={editing.locality} onChange={(e) => setEditing({ ...editing, locality: e.target.value })} className={`mt-1.5 ${inputCls}`} data-testid="vac-locality-input"/></div>
              <div>
                <label className="text-sm font-medium">Tipo de serviço</label>
                <select value={editing.service_type} onChange={(e) => setEditing({ ...editing, service_type: e.target.value })} className={`mt-1.5 ${inputCls}`} data-testid="vac-type-select">
                  <option value="continuous">🟢 Continuidade</option>
                  <option value="punctual">🔵 Pontual</option>
                </select>
              </div>
              <div><label className="text-sm font-medium">Horário</label><input required value={editing.schedule} onChange={(e) => setEditing({ ...editing, schedule: e.target.value })} className={`mt-1.5 ${inputCls}`}/></div>
              <div><label className="text-sm font-medium">Horas / semana</label><input value={editing.hours || ""} onChange={(e) => setEditing({ ...editing, hours: e.target.value })} className={`mt-1.5 ${inputCls}`}/></div>
              <div className="md:col-span-2"><label className="text-sm font-medium">Requisitos</label><textarea required value={editing.requirements} onChange={(e) => setEditing({ ...editing, requirements: e.target.value })} className={`mt-1.5 ${inputCls} min-h-[100px]`}/></div>
              <div><label className="text-sm font-medium">Data prevista de início</label><input type="date" value={editing.start_date || ""} onChange={(e) => setEditing({ ...editing, start_date: e.target.value })} className={`mt-1.5 ${inputCls}`}/></div>
              <div><label className="text-sm font-medium">Data de término <span className="text-xs text-[var(--ink-soft)]">(opcional)</span></label><input type="date" value={editing.end_date || ""} onChange={(e) => setEditing({ ...editing, end_date: e.target.value })} className={`mt-1.5 ${inputCls}`} data-testid="vac-end-date-input"/></div>
              <div><label className="text-sm font-medium">Vagas</label><input type="number" min="1" value={editing.slots} onChange={(e) => setEditing({ ...editing, slots: e.target.value })} className={`mt-1.5 ${inputCls}`} data-testid="vac-slots-input"/></div>
              {editing.service_type === "continuous" ? (
                <div className="md:col-span-2"><label className="text-sm font-medium">Remuneração média mensal (€)</label><input type="number" step="0.01" value={editing.monthly_avg} onChange={(e) => setEditing({ ...editing, monthly_avg: e.target.value })} className={`mt-1.5 ${inputCls}`} data-testid="vac-monthly-input"/></div>
              ) : (
                <div className="md:col-span-2"><label className="text-sm font-medium">Valor total (€)</label><input type="number" step="0.01" value={editing.total_value} onChange={(e) => setEditing({ ...editing, total_value: e.target.value })} className={`mt-1.5 ${inputCls}`} data-testid="vac-total-input"/></div>
              )}
              {editing.id && (
                <div>
                  <label className="text-sm font-medium">Estado</label>
                  <select value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })} className={`mt-1.5 ${inputCls}`}>
                    <option value="open">Aberta</option>
                    <option value="closed">Fechada</option>
                  </select>
                </div>
              )}
              <label className="flex items-center gap-2 md:col-span-2">
                <input type="checkbox" checked={editing.referral_eligible} onChange={(e) => setEditing({ ...editing, referral_eligible: e.target.checked })}/>
                <span className="text-sm">Elegível para referenciação</span>
              </label>
            </div>
            <div className="p-6 border-t border-[var(--border)] flex justify-end gap-2">
              <button type="button" onClick={closeEdit} className="pill-btn pill-ghost">Cancelar</button>
              <button disabled={busy} className="pill-btn pill-primary" data-testid="vac-save-btn">{busy ? "A guardar…" : "Guardar"}</button>
            </div>
          </form>
        </div>
      )}
      {share && <ShareModal vacancy={share} onClose={() => setShare(null)}/>}

      {importOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm grid place-items-center p-4" onClick={closeImport}>
          <div className="bg-white rounded-3xl w-full max-w-3xl max-h-[92vh] overflow-hidden flex flex-col shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 flex items-center justify-between border-b border-[var(--border)]">
              <div>
                <div className="eyebrow">Importação em massa</div>
                <h2 className="font-heading text-2xl">Importar vagas por JSON</h2>
              </div>
              <button onClick={closeImport} className="pill-btn pill-ghost !py-2 !px-3" data-testid="admin-import-close-btn"><X className="w-4 h-4"/></button>
            </div>
            <div className="p-6 overflow-y-auto space-y-4">
              <div className="text-sm text-[var(--ink-soft)] leading-relaxed">
                Cole abaixo um JSON com o campo <code className="px-1.5 py-0.5 rounded bg-[var(--muted)] text-xs">"vacancies"</code>{" "}
                contendo uma lista de vagas. Vagas com o mesmo <strong>título</strong> e <strong>localidade</strong> já existentes serão automaticamente ignoradas — pode reimportar quantas vezes quiser sem duplicar.
              </div>
              <button onClick={downloadTemplate} className="pill-btn pill-ghost" data-testid="admin-import-template-btn">
                <Download className="w-4 h-4"/> Descarregar template
              </button>
              <textarea
                value={importJson}
                onChange={(e) => setImportJson(e.target.value)}
                rows={12}
                spellCheck={false}
                placeholder='{"vacancies": [{ "title": "...", "locality": "...", "service_type": "continuous", "schedule": "...", "requirements": "..." }]}'
                className="w-full px-3 py-2.5 rounded-xl bg-[var(--muted)] border border-[var(--border)] focus:outline-none focus:ring-2 focus:ring-[var(--forest)] font-mono text-xs"
                data-testid="admin-import-textarea"
              />
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={importNotify} onChange={(e) => setImportNotify(e.target.checked)} data-testid="admin-import-notify-toggle"/>
                <span>Notificar referenciadores por email/aviso após importar</span>
              </label>
              {importResult && (
                <div className="rounded-2xl border border-[var(--border)] p-4 space-y-2 text-sm" data-testid="admin-import-result">
                  <div><strong className="text-[var(--forest)]">{importResult.created_count}</strong> vaga(s) criada(s) · <strong>{importResult.skipped_count}</strong> ignorada(s) por já existirem.</div>
                  {importResult.created?.length > 0 && (
                    <ul className="text-xs text-[var(--ink-soft)] list-disc pl-5 space-y-0.5">
                      {importResult.created.map((c) => <li key={c.id}>{c.title} — {c.locality}</li>)}
                    </ul>
                  )}
                </div>
              )}
            </div>
            <div className="p-6 border-t border-[var(--border)] flex justify-end gap-2">
              <button onClick={closeImport} className="pill-btn pill-ghost">Fechar</button>
              <button onClick={submitImport} disabled={busy || !importJson.trim()} className="pill-btn pill-primary" data-testid="admin-import-submit-btn">
                <Upload className="w-4 h-4"/> {busy ? "A importar…" : "Importar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
